import { useMemo } from 'react';

import { Col, Flex, Grid, Row, Typography, theme } from 'antd';
import { FilterCardEcho, AudienceDownloadTriggerEcho } from '@/components';
import { VisibleFilterKey } from '@/components/cards/FilterCard/Filters';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { twMerge } from 'tailwind-merge';
import { useProfilesQuery } from '@/services/revenueApi';
import { useFilterContext, useDerivedNetworkContext } from '@/context';
import { APPLE_PODCASTS_ONLY_PLAYER_SECTION, hasHlsEnabledShow } from '@/utils';
import NoAccessMessage from '@/components/PageWrapper/NoAccessMessage';
import AgeCardEcho from './AgeCardEcho';
import LocationCardEcho from './LocationCardEcho';
import TopDevicesCardEcho from './TopDevicesCardEcho';
import TopPlayersCardEcho from './TopPlayersCardEcho';
import UniquesCardEcho from './UniqueListenersCard/UniquesCardEcho';
import { renderAudienceBanner } from './renderAudienceBanner';
import { useSyncUrlToFilters } from '@/hooks/useSyncUrlToFilters';
import useHasHlsEnabledNetwork from '@/hooks/useHasHlsEnabledNetwork';
import { isSocialPlatform } from '@/types/api/platforms';
import UniquesOverTimeCardEcho from './UniquesOverTimeCardEcho';
import useFetchQuery from '@/hooks/useFetchQuery';
import SocialProfilesCardEcho from './SocialProfilesCard/SocialProfilesCardEcho';
import GenderCardEcho from './GenderCardEcho';
import FollowersCardEcho from './FollowersCardEcho';

const AudiencePageEcho = () => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isMediumScreen = Boolean(screens.md);
  const isLargeScreen = Boolean(screens.lg);
  const { filters } = useFilterContext();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { shows } = selectedNetwork;
  const {
    audienceTab: isAudienceTabEnabled = false,
    contentPerformanceHls = false,
    audiencePageDataBanners = [],
  } = useFlags<{
    audienceTab?: boolean;
    contentPerformanceHls?: boolean;
    audiencePageDataBanners?: string[];
  }>();
  const hasHlsNetwork = useHasHlsEnabledNetwork();
  const isAudienceEnabled = isAudienceTabEnabled || (contentPerformanceHls && hasHlsNetwork);
  const { isSynced } = useSyncUrlToFilters({ requireSingleShow: true });
  const { queryResult } = useFetchQuery(useProfilesQuery, {
    enabled: filters.selectedShows.length > 0 && isSynced,
  });
  const hasHlsInSelection = isSynced && hasHlsEnabledShow([selectedNetwork], filters.selectedShows);
  const platformsWithoutData = useMemo(() => {
    if (queryResult.isPending || queryResult.isError) {
      return [];
    }

    const connectedSocialProfiles =
      queryResult.data?.profiles.filter((profile) => isSocialPlatform(profile.platform)) ?? [];

    if (connectedSocialProfiles.length === 0) return [];

    const profilesWithNoData = connectedSocialProfiles.filter((p) => !p.earliest_data_date);

    return [...new Set(profilesWithNoData.map((p) => p.platform))];
  }, [queryResult.isPending, queryResult.isError, queryResult.data]);

  const platformsBeforeEarliestData = useMemo(() => {
    if (queryResult.isPending || queryResult.isError) return [];
    const rangeFrom = filters.dateRange.from;
    return (queryResult.data?.profiles ?? [])
      .filter(
        (p) =>
          isSocialPlatform(p.platform) && p.earliest_data_date && rangeFrom < p.earliest_data_date
      )
      .map((p) => ({
        platform: p.platform,
        earliestDataDate: p.earliest_data_date ?? rangeFrom,
      }));
  }, [queryResult.isPending, queryResult.isError, queryResult.data, filters.dateRange.from]);

  // Hour-precision UTC prefix (YYYY-MM-DDTHH). Sliced to a string so it's a stable
  // useMemo dependency that only changes once an hour, rather than every render.
  // expires_at is currently a truncated UTC date (YYYY-MM-DD) but will become a full
  // UTC timestamp upstream; both compare correctly against this prefix lexicographically.
  const nowUtcPrefix = new Date().toISOString().slice(0, 13);
  const { reauthRequiredPlatforms, reauthRequiredLatestExpiry } = useMemo(() => {
    if (queryResult.isPending || queryResult.isError) {
      return { reauthRequiredPlatforms: [], reauthRequiredLatestExpiry: undefined };
    }
    const expiredProfiles =
      queryResult.data?.profiles.filter(
        (p) => isSocialPlatform(p.platform) && p.expires_at != null && p.expires_at < nowUtcPrefix
      ) ?? [];
    return {
      reauthRequiredPlatforms: [...new Set(expiredProfiles.map((p) => p.platform))],
      // Latest expiry among the *expired* platforms only — used as the dismissal
      // threshold so a newer expiry re-shows the banner. Including non-expired tokens
      // here would push the threshold into the future and make dismissal impossible.
      reauthRequiredLatestExpiry: expiredProfiles
        .map((p) => p.expires_at as string)
        .reduce<
          string | undefined
        >((latest, e) => (latest == null || e > latest ? e : latest), undefined),
    };
  }, [queryResult.isPending, queryResult.isError, queryResult.data, nowUtcPrefix]);

  // Pick the highest-priority banner whose activation criteria are met (or none).
  const audienceBanner = renderAudienceBanner({
    isPending: queryResult.isPending,
    enabledBanners: audiencePageDataBanners,
    showId: filters.selectedShows[0] ?? '',
    networkId: selectedNetwork?.id,
    platformsWithoutData,
    isOnMarketplace: queryResult.data?.show?.is_on_marketplace,
    hasMarketplacePodcastProfile: queryResult.data?.show?.has_marketplace_podcast_profile,
    hasPhylloConfigured: queryResult.data?.show?.has_phyllo_configured,
    platformsBeforeEarliestData,
    reauthRequiredPlatforms,
    reauthRequiredLatestExpiry,
  });

  const noOfShows = shows?.length ?? 0;
  const visibleFilters: VisibleFilterKey[] =
    noOfShows > 1
      ? [VisibleFilterKey.Networks, VisibleFilterKey.Shows, VisibleFilterKey.DateRange]
      : [VisibleFilterKey.Networks, VisibleFilterKey.DateRange];

  if ((!isAudienceEnabled && selectedNetwork?.id) || (shows && shows.length < 1)) {
    return <NoAccessMessage />;
  }

  const INVERSE_TEXT_STYLE = { color: token.colorTextLightSolid };

  return (
    <>
      <Flex
        vertical={!isMediumScreen}
        justify="space-between"
        style={{
          width: '100%',
          paddingTop: token.paddingLG,
          paddingBottom: token.marginXL + token.marginXS,
        }}
      >
        <Typography.Title level={1} style={{ marginBottom: 0 }}>
          Audience
        </Typography.Title>
        <Flex justify="flex-end" style={{ width: isMediumScreen ? undefined : '100%' }}>
          <AudienceDownloadTriggerEcho />
        </Flex>
      </Flex>

      <FilterCardEcho visibleFilters={visibleFilters} />

      <Row gutter={[token.marginLG, token.marginLG]} style={{ marginBottom: token.marginLG }}>
        <Col span={24}>
          <SocialProfilesCardEcho
            className="w-full p-0 overflow-hidden"
            hideProfileBanner={!!audiencePageDataBanners?.length}
          />
          {audienceBanner}
        </Col>
      </Row>

      <Flex
        vertical={!isLargeScreen}
        gap={token.margin}
        style={{ width: '100%', marginBottom: token.marginLG }}
      >
        {hasHlsInSelection && (
          <UniquesCardEcho
            label="Unique listeners & viewers"
            mediaType="listenersAndViewers"
            tooltipContent={
              <Flex vertical gap={token.marginXS} style={{ maxWidth: 320 }}>
                <Typography.Text strong style={INVERSE_TEXT_STYLE}>
                  7 days total unique listeners and viewers
                </Typography.Text>
                <Typography.Paragraph style={{ ...INVERSE_TEXT_STYLE, margin: 0 }}>
                  The total number of individual people who listened to <strong>OR</strong> viewed
                  your podcast in the last 7 days of the period you selected.
                </Typography.Paragraph>
                <Typography.Paragraph style={{ ...INVERSE_TEXT_STYLE, margin: 0 }}>
                  May be less than the sum of the individual listener and viewer stats, as someone
                  who both listened and watched is only counted once.
                </Typography.Paragraph>
              </Flex>
            }
            podcastPlayersTooltipContent={
              <Flex vertical gap={token.marginXS}>
                <Typography.Text style={INVERSE_TEXT_STYLE}>Includes:</Typography.Text>
                <ul
                  style={{
                    margin: 0,
                    paddingInlineStart: token.paddingLG,
                    color: token.colorTextLightSolid,
                    listStyleType: 'disc',
                  }}
                >
                  <li style={{ marginBottom: token.marginXXS }}>
                    <Typography.Text style={INVERSE_TEXT_STYLE}>
                      <Typography.Text strong style={INVERSE_TEXT_STYLE}>
                        all podcast apps or websites
                      </Typography.Text>{' '}
                      people use to listen to the audio version of your show (Spotify, Apple
                      Podcasts, Castbox, Amazon Music, Pocket Casts, etc.)
                    </Typography.Text>
                  </li>
                  <li>
                    <Typography.Text style={INVERSE_TEXT_STYLE}>
                      Views of the video version of your show are from Apple Podcasts only.
                    </Typography.Text>
                  </li>
                </ul>
              </Flex>
            }
            className={twMerge('w-full', isLargeScreen && 'flex-[1_1_0%]')}
          />
        )}
        <UniquesCardEcho
          label="Unique listeners"
          mediaType="listeners"
          tooltipContent={
            <Flex vertical gap={token.marginXS} style={{ maxWidth: 320 }}>
              <Typography.Text strong style={INVERSE_TEXT_STYLE}>
                7 days total unique listeners
              </Typography.Text>
              <Typography.Paragraph style={{ ...INVERSE_TEXT_STYLE, margin: 0 }}>
                The total number of individual people who listened to your podcast in the last 7
                days of the period you selected.
              </Typography.Paragraph>
            </Flex>
          }
          podcastPlayersTooltipContent={
            <Typography.Text style={INVERSE_TEXT_STYLE}>
              Includes{' '}
              <Typography.Text strong style={INVERSE_TEXT_STYLE}>
                all audio podcast apps or websites
              </Typography.Text>{' '}
              listeners use to play your content (Spotify, Apple Podcasts, Castbox, Amazon Music,
              Pocket Casts, etc.)
            </Typography.Text>
          }
          className={twMerge('w-full', isLargeScreen && 'flex-[1_1_0%]')}
        />
        {hasHlsInSelection && (
          <UniquesCardEcho
            label="Unique viewers"
            mediaType="viewers"
            playersLabel={APPLE_PODCASTS_ONLY_PLAYER_SECTION.label}
            playersOverride={[...APPLE_PODCASTS_ONLY_PLAYER_SECTION.players]}
            tooltipContent={
              <Flex vertical gap={token.marginXS} style={{ maxWidth: 320 }}>
                <Typography.Text strong style={INVERSE_TEXT_STYLE}>
                  7 days total unique viewers
                </Typography.Text>
                <Typography.Paragraph style={{ ...INVERSE_TEXT_STYLE, margin: 0 }}>
                  The total number of individual people who viewed your podcast in the last 7 days
                  of the period you selected.
                </Typography.Paragraph>
              </Flex>
            }
            podcastPlayersTooltipContent={
              <Typography.Text style={INVERSE_TEXT_STYLE}>
                Video data reflects Apple Podcasts only, with more comprehensive data to come in the
                future.
              </Typography.Text>
            }
            className={twMerge('w-full', isLargeScreen && 'flex-[1_1_0%]')}
          />
        )}
      </Flex>

      <Row gutter={[token.marginLG, token.marginLG]} style={{ marginBottom: token.marginLG }}>
        <Col span={24}>
          <UniquesOverTimeCardEcho hasHlsInSelection={hasHlsInSelection} />
        </Col>
        <Col span={24}>
          <Flex
            wrap={isLargeScreen ? 'wrap' : undefined}
            vertical={!isLargeScreen}
            gap={token.marginLG}
            style={{ width: '100%' }}
          >
            <GenderCardEcho
              className={twMerge('w-full', isLargeScreen && 'min-w-[320px] flex-[1_1_280px]')}
            />
            <AgeCardEcho
              className={twMerge('w-full', isLargeScreen && 'min-w-[320px] flex-[1_1_280px]')}
            />
            <FollowersCardEcho
              className={twMerge('w-full', isLargeScreen && 'min-w-[320px] flex-[1_1_280px]')}
            />
          </Flex>
        </Col>
        <Col span={24}>
          <LocationCardEcho />
        </Col>
      </Row>

      <Row gutter={[token.marginLG, token.marginLG]}>
        <Col xs={24} lg={12}>
          <TopPlayersCardEcho className="h-full" />
        </Col>
        <Col xs={24} lg={12}>
          <TopDevicesCardEcho className="h-full" />
        </Col>
      </Row>
    </>
  );
};

export default AudiencePageEcho;
